import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Shield, Users, Briefcase, Flag, Star, RefreshCw, BarChart3 } from "lucide-react";
import AdminAnalytics from "@/components/AdminAnalytics";

export default function AdminDashboard() {
  const [tab, setTab] = useState("stats");
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [sources, setSources] = useState([]);
  const [jobs, setJobs] = useState([]);

  const load = async () => {
    const [s, u, r, so, j] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users"),
      api.get("/admin/reports"),
      api.get("/admin/sources"),
      api.get("/jobs", { params: { limit: 100 } }),
    ]);
    setStats(s.data); setUsers(u.data.users || []); setReports(r.data.reports || []); setSources(so.data.sources || []); setJobs(j.data.jobs || []);
  };
  useEffect(()=>{ load(); }, []);

  const ban = async (id, banned) => { try { await api.put(`/admin/users/${id}/ban`, null, { params: { banned } }); toast(banned ? "Banned":"Unbanned"); load(); } catch(e){ toast.error(formatApiError(e)); } };
  const resolve = async (id) => { try { await api.put(`/admin/reports/${id}/resolve`); toast("Resolved"); load(); } catch(e){ toast.error(formatApiError(e)); } };
  const feature = async (id, featured) => { try { await api.put(`/admin/jobs/${id}/feature`, null, { params: { featured } }); toast(featured ? "Featured":"Unfeatured"); load(); } catch(e){ toast.error(formatApiError(e)); } };
  const sync = async (name) => { try { await api.post(`/admin/sources/${name}/sync`); toast.success(`Syncing ${name}…`); setTimeout(load, 3500); } catch(e){ toast.error(formatApiError(e)); } };

  return (
    <div className="nh-container py-10">
      <Toaster position="top-center"/>
      <h1 className="font-display font-black text-4xl flex items-center gap-2"><Shield/> Admin Console</h1>

      <div className="flex flex-wrap gap-2 mt-6">
        {["stats", "analytics", "users", "reports", "jobs", "sources"].map(t=>(
          <button key={t} onClick={()=>setTab(t)} className={`nh-btn text-xs ${tab===t ? "nh-btn-secondary" : "nh-btn-ghost"}`} data-testid={`admin-tab-${t}`}>
            {t.toUpperCase()}
          </button>
        ))}
      </div>

      {tab === "analytics" && <AdminAnalytics/>}

      {tab === "stats" && (
        <div className="mt-6 grid md:grid-cols-4 gap-4">
          {Object.entries(stats).map(([k,v])=>(
            <div key={k} className="nh-card p-5">
              <div className="text-xs uppercase font-bold text-[var(--nh-muted)]">{k.replace(/_/g," ")}</div>
              <div className="text-4xl font-display font-black mt-2">{v}</div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div className="mt-6 nh-card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black text-white text-left"><tr><th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
            <tbody>
              {users.map(u=>(
                <tr key={u.id} className="border-t-2 border-black">
                  <td className="p-3 font-bold">{u.name}</td>
                  <td className="p-3">{u.email}</td>
                  <td className="p-3"><span className="nh-tag bg-white">{u.role}</span></td>
                  <td className="p-3">{u.banned ? <span className="nh-tag bg-red-200">Banned</span> : <span className="nh-tag bg-[var(--nh-mint)]">Active</span>}</td>
                  <td className="p-3">
                    {u.role !== "admin" && (
                      <button onClick={()=>ban(u.id, !u.banned)} className="nh-btn nh-btn-ghost text-xs">
                        {u.banned ? "Unban" : "Ban"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "reports" && (
        <div className="mt-6 space-y-3">
          {reports.length === 0 && <p className="text-sm text-[var(--nh-muted)]">No reports.</p>}
          {reports.map(r=>(
            <div key={r.id} className="nh-card p-5 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2"><Flag size={14}/> <span className="font-bold">{r.target_type}</span> · <code className="text-xs">{r.target_id}</code></div>
                <p className="text-sm mt-1">{r.reason}</p>
                <p className="text-xs text-[var(--nh-muted)] mt-1">{new Date(r.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`nh-tag ${r.status === "open" ? "bg-red-200" : "bg-[var(--nh-mint)]"}`}>{r.status}</span>
                {r.status === "open" && <button onClick={()=>resolve(r.id)} className="nh-btn nh-btn-primary text-xs">Resolve</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "jobs" && (
        <div className="mt-6 nh-card p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-black text-white text-left"><tr><th className="p-3">Title</th><th className="p-3">Source</th><th className="p-3">Featured</th><th className="p-3">Actions</th></tr></thead>
            <tbody>
              {jobs.map(j=>(
                <tr key={j.id} className="border-t-2 border-black">
                  <td className="p-3 font-bold">{j.title} <span className="text-xs text-[var(--nh-muted)]">— {j.company_name}</span></td>
                  <td className="p-3"><span className="nh-tag bg-white">{j.source}</span></td>
                  <td className="p-3">{j.featured ? <Star size={16}/> : "—"}</td>
                  <td className="p-3"><button onClick={()=>feature(j.id, !j.featured)} className="nh-btn nh-btn-ghost text-xs">{j.featured ? "Unfeature" : "Feature"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "sources" && (
        <div className="mt-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <p className="text-sm text-[var(--nh-muted)]">
              External job sources. Auto-sync runs every 6 hours. Manual sync + cleanup below.
            </p>
            <button
              onClick={async ()=>{
                if (!confirm("Purge any imported jobs that mention degrees/senior/experience gates? Sample & employer jobs are safe.")) return;
                try {
                  const { data } = await api.post("/admin/cleanup-non-youth");
                  toast.success(`Removed ${data.removed} · capped ${data.updated_max_age} · scanned ${data.scanned}`);
                  load();
                } catch(e){ toast.error(formatApiError(e)); }
              }}
              className="nh-btn nh-btn-primary text-xs"
              data-testid="admin-cleanup-non-youth"
            >
              🧹 Clean Non-Youth Jobs
            </button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {["remotive", "adzuna", "usajobs", "jooble"].map((s)=>{
              const meta = sources.find(x=>x.name===s);
              return (
                <div key={s} className="nh-card p-5" data-testid={`admin-source-${s}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-black text-xl uppercase">{s}</h3>
                    {meta?.last_errors > 0 && <span className="nh-tag bg-red-200">Err</span>}
                  </div>
                  <div className="mt-3 text-xs text-[var(--nh-muted)] space-y-1">
                    <div>Last synced: <b>{meta?.last_synced ? new Date(meta.last_synced).toLocaleString() : "never"}</b></div>
                    <div>Imported: <b>{meta?.last_imported ?? 0}</b></div>
                    <div>Updated: <b>{meta?.last_updated ?? 0}</b></div>
                    <div>Duplicates: <b>{meta?.last_duplicates ?? 0}</b></div>
                    <div>Errors: <b className={meta?.last_errors ? "text-red-600" : ""}>{meta?.last_errors ?? 0}</b></div>
                    {meta?.last_error_message && <div className="text-red-600 truncate">{meta.last_error_message}</div>}
                  </div>
                  <button onClick={()=>sync(s)} className="nh-btn nh-btn-primary text-xs mt-3 w-full" data-testid={`admin-sync-${s}`}>
                    <RefreshCw size={14}/> Sync now
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
